/**
 * Welcome - Page d'accueil / Porte d'entrée de l'application
 *
 * Permet au vendeur de choisir son espace de travail :
 * - POS : Caisse, préparation, live shopping
 * - Stocks : Gestion des stocks, réception fournisseurs
 *
 * L'accès aux boutons dépend des workstations assignées au vendeur.
 */
import { useNavigate } from 'react-router-dom';
import { usePOSStore, WORKSTATION_CODES } from '../hooks/usePOSStore';

export default function Welcome() {
  const navigate = useNavigate();
  const vendorName = usePOSStore((state) => state.currentVendorName);
  const vendorRole = usePOSStore((state) => state.currentVendorRole);
  const hasWorkstationAccess = usePOSStore((state) => state.hasWorkstationAccess);

  // Vérifier les accès
  const canAccessPOS = hasWorkstationAccess(WORKSTATION_CODES.POS) ||
                       hasWorkstationAccess(WORKSTATION_CODES.PREPARATION) ||
                       hasWorkstationAccess(WORKSTATION_CODES.LIVE);
  const canAccessStocks = hasWorkstationAccess(WORKSTATION_CODES.STOCKS);

  // Si pas de vendeur connecté, rediriger vers le POS pour la sélection
  if (!vendorName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">FOLIES POS</h1>
          <p className="text-gray-600 mb-6">
            Veuillez d'abord vous connecter pour accéder à votre espace de travail.
          </p>
          <button
            onClick={() => navigate('/pos')}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
      {/* Header avec info vendeur */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">FOLIES POS</h1>
        <div className="flex items-center justify-center gap-2 text-gray-600">
          <span className="text-2xl">
            {vendorRole === 'ADMIN' ? '👑' :
             vendorRole === 'MANAGER' ? '⭐' :
             vendorRole === 'CASHIER_PLUS' ? '🎯' : '👤'}
          </span>
          <span className="text-lg font-medium">{vendorName}</span>
        </div>
      </div>

      {/* Choix de l'espace de travail */}
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
        <h2 className="text-xl font-semibold text-gray-700 mb-6 text-center">
          Choisissez votre espace de travail
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Bouton POS */}
          <button
            onClick={() => navigate('/pos')}
            disabled={!canAccessPOS}
            className={`p-8 rounded-2xl border-2 transition-all flex flex-col items-center gap-4 ${
              canAccessPOS
                ? 'border-blue-200 hover:border-blue-500 hover:shadow-lg bg-blue-50 hover:bg-blue-100 cursor-pointer'
                : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
            }`}
          >
            <span className="text-6xl">🛒</span>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">Point de Vente</h3>
              <p className="text-sm text-gray-500 mt-2">
                Caisse, préparation commandes, live shopping
              </p>
            </div>
            {!canAccessPOS && (
              <span className="text-xs text-red-500 mt-2">
                Accès non autorisé
              </span>
            )}
          </button>

          {/* Bouton Stocks */}
          <button
            onClick={() => navigate('/stocks')}
            disabled={!canAccessStocks}
            className={`p-8 rounded-2xl border-2 transition-all flex flex-col items-center gap-4 ${
              canAccessStocks
                ? 'border-green-200 hover:border-green-500 hover:shadow-lg bg-green-50 hover:bg-green-100 cursor-pointer'
                : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
            }`}
          >
            <span className="text-6xl">📊</span>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">Gestion Stocks</h3>
              <p className="text-sm text-gray-500 mt-2">
                Réception, inventaire, mouvements
              </p>
            </div>
            {!canAccessStocks && (
              <span className="text-xs text-red-500 mt-2">
                Accès non autorisé
              </span>
            )}
          </button>
        </div>

        {/* Bouton Dashboard pour ADMIN */}
        {vendorRole === 'ADMIN' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
            >
              <span>⚙️</span>
              <span>Accéder au Dashboard (Administration)</span>
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="mt-8 text-gray-400 text-sm">
        Sélectionnez un espace pour commencer
      </p>
    </div>
  );
}
